import { useAuth } from '@/auth/Auth';
import { usePermissions } from '@/auth/PermissionsContext';
import { ReportsService } from '@/services/reports.service';
import { RetentionResponse, WalletService } from '@/services/wallet.service';
import useCurrencyStore from '@/store/useCurrencyStore';
import { formatDateForAPI, safeStringToDate } from '@/utils/dateHelpers';
import { useQuery } from '@tanstack/react-query';
import { useQueryState } from 'nuqs';
import React, { useCallback, useMemo } from 'react';
import { DateRange } from 'react-day-picker';

interface SalesByCountryAPI {
  country_code: string;
  country_name: string;
  flag_emoji: string;
  count: number;
  amount: number;
  amount_formatted: string;
  percentage: number;
  count_percentage: number;
  average_order_value: number;
  average_order_value_formatted: string;
  min_order_value: number;
  min_order_value_formatted: string;
  max_order_value: number;
  max_order_value_formatted: string;
}

interface SalesByCountryResponse {
  data: SalesByCountryAPI[];
  summary: {
    total_countries: number;
    total_sales: number;
    total_amount: number;
    total_amount_formatted: string;
  };
  meta: {
    start_date: string;
    end_date: string;
    currency: string | null;
  };
}

export interface RetentionItem {
  type: string;
  type_label: string;
  total: number;
  total_formatted: string;
}

type RetentionItems = {
  items: RetentionItem[];
};

interface MetricsData {
  reports: any[];
  paidSales: any;
  metrics: any;
  nextReward: RewardProgress;
  retention: RetentionItems;
  totalAmount?: string;
  salesByCountry: SalesByCountryAPI[];
}

interface RewardProgress {
  withdrawnValue: string;
  targetValue: number;
  progress: number;
  currentLevelImage: string;
  isMaxLevelReached?: boolean;
}

const REWARD_LEVELS = [
  { value: 2000, image: '/placa2k.svg' },
  { value: 10000, image: '/placa10k.svg' },
  { value: 20000, image: '/placa20k.svg' },
  { value: 100000, image: '/placa100k.svg' },
  { value: 200000, image: '/placa200k.svg' },
  { value: 500000, image: '/placa500k.svg' },
] as const;


const QUERY_CONFIG = {
  staleTime: 1000 * 60 * 5, // 5 minutos
  refetchOnWindowFocus: false,
} as const;

const calculateRewardProgress = (
  totalWithdrawn: string,
  totalFormatted: string,
  currency: string,
  conversions?: { from: string; to: string; rate: number }[]
): RewardProgress => {
  const withdrawnRaw = Number(totalWithdrawn) / 100;

  // Converter para USD para cálculo consistente dos níveis
  let withdrawnInUSD = withdrawnRaw;
  let toUSDRate: number | null = null;

  if (currency !== 'USD' && conversions?.length) {
    // Tentar encontrar taxa direta: from=currency, to=USD
    const direct = conversions.find((c) => c.from === currency && c.to === 'USD');
    if (direct && direct.rate > 0) {
      toUSDRate = direct.rate;
    } else {
      // Tentar taxa inversa: from=USD, to=currency → inverter
      const inverse = conversions.find((c) => c.from === 'USD' && c.to === currency);
      if (inverse && inverse.rate > 0) {
        toUSDRate = 1 / inverse.rate;
      }
    }

    if (toUSDRate) {
      withdrawnInUSD = withdrawnRaw * toUSDRate;
    }
  }

  let nextRewardIndex = -1;
  let nextRewardImage = '/placa2k.svg';
  let targetValueUSD: number = REWARD_LEVELS[0].value;
  let isMaxLevelReached = false;

  for (let i = 0; i < REWARD_LEVELS.length; i++) {
    if (withdrawnInUSD < REWARD_LEVELS[i].value) {
      nextRewardIndex = i;
      nextRewardImage = REWARD_LEVELS[i].image;
      targetValueUSD = REWARD_LEVELS[i].value;
      break;
    }
  }

  if (nextRewardIndex === -1) {
    const maxLevel = REWARD_LEVELS[REWARD_LEVELS.length - 1];
    nextRewardImage = maxLevel.image;
    targetValueUSD = maxLevel.value;
    isMaxLevelReached = true;
  }

  let progress: number;

  if (isMaxLevelReached) {
    progress = 100;
  } else {
    progress = (withdrawnInUSD / targetValueUSD) * 100;
  }

  progress = Math.max(0, Math.min(100, progress));

  // Converter target para a moeda selecionada usando a taxa da API
  let targetValueDisplay = targetValueUSD;
  if (toUSDRate && toUSDRate > 0) {
    targetValueDisplay = targetValueUSD / toUSDRate;
  }

  return {
    withdrawnValue: totalFormatted,
    targetValue: targetValueDisplay,
    progress: Math.round(progress * 100) / 100,
    currentLevelImage: nextRewardImage,
    isMaxLevelReached,
  };
};

const createDefaultReward = (): RewardProgress => ({
  withdrawnValue: 'R$ 0,00',
  targetValue: REWARD_LEVELS[0].value,
  progress: 0,
  currentLevelImage: '/placa2k.svg',
});

const transformReportsData = (reports: any[]) => {
  return (
    reports?.map((report) => ({
      hour: report.date_formatted,
      tooltipLabel: report.paid_formatted,
      value: report.paid ?? 0,
      amount: report.paid ?? 0,
    })) || []
  );
};

export function useMetrics() {
  const initialFromRef = React.useRef(new Date().toISOString());
  const initialToRef = React.useRef(new Date().toISOString());

  const { authData } = useAuth();
  const { hasPermission, hasAnyPermission } = usePermissions();

  const canViewSaleData = hasPermission('metrics.view_sale_data');
  const canViewWallet   = hasAnyPermission([
    'wallet.view_available_balance',
    'wallet.view_balance_to_release',
    'wallet.view_retention_balance',
    'wallet.view_total_withdrawn',
  ]);

  const [from, setFrom] = useQueryState('from', {
    defaultValue: initialFromRef.current,
    history: 'replace',
  });
  const [to, setTo] = useQueryState('to', {
    defaultValue: initialToRef.current,
    history: 'replace',
  });

  const currency = useCurrencyStore((s) => s.currency);

  const setFromIfChanged = React.useCallback(
    (iso: string) => {
      if (iso && iso !== from) setFrom(iso);
    },
    [from, setFrom]
  );

  const setToIfChanged = React.useCallback(
    (iso: string) => {
      if (iso && iso !== to) setTo(iso);
    },
    [to, setTo]
  );

  const dateRange = useMemo(() => {
    const fromDate = safeStringToDate(from);
    const toDate = safeStringToDate(to);

    return {
      from: fromDate,
      to: toDate,
    };
  }, [from, to]);

  const setDateRange = React.useCallback(
    (range: DateRange | undefined) => {
      if (!range) return;

      const fromIso = range.from?.toISOString();
      const toIso = (range.to ?? range.from)?.toISOString();

      if (fromIso) setFromIfChanged(fromIso);
      if (toIso) setToIfChanged(toIso);
    },
    [setFromIfChanged, setToIfChanged]
  );

  const { startDate, endDate } = useMemo(() => {
    const fromDate = dateRange?.from || new Date();
    const toDate = dateRange?.to || new Date();

    return {
      startDate: formatDateForAPI(fromDate),
      endDate: formatDateForAPI(toDate),
    };
  }, [dateRange]);

  const {
    data: metricsData,
    isPending: isLoading,
    error,
  } = useQuery({
    queryKey: ['metrics-data', startDate, endDate, currency],
    enabled: canViewSaleData || canViewWallet,
    queryFn: async (): Promise<MetricsData> => {
      try {
        const [reports, paidSales, metrics, totals, retention, salesByCountryResult] =
          await Promise.allSettled([
            canViewSaleData
              ? ReportsService.getReports({ startDate, endDate, currency })
              : Promise.resolve(null),
            canViewSaleData
              ? ReportsService.getReportsSalesByStatus({
                  startDate,
                  endDate,
                  filterParams: { status: ['paid'], currency },
                  q: undefined,
                })
              : Promise.resolve(null),
            canViewSaleData
              ? ReportsService.getMetrics({ startDate, endDate, currency })
              : Promise.resolve(null),
            canViewWallet
              ? WalletService.getTotalsWithdrawals(currency, true)
              : Promise.resolve(null),
            canViewWallet
              ? WalletService.getRetention(currency)
              : Promise.resolve(null),
            canViewSaleData
              ? ReportsService.getSalesByCountry({ start_date: startDate, end_date: endDate, currency })
              : Promise.resolve(null),
          ]);

        const reportsData =
          reports.status === 'fulfilled'
            ? transformReportsData(reports.value?.data)
            : [];

        const paidSalesData =
          paidSales.status === 'fulfilled' ? paidSales.value : null;

        const metricsData =
          metrics.status === 'fulfilled' ? metrics.value : null;

        const totalsData = totals.status === 'fulfilled' ? totals.value : null;

        const retentionData =
          retention.status === 'fulfilled' ? retention.value : [];

        const salesByCountryData =
          salesByCountryResult.status === 'fulfilled'
            ? (salesByCountryResult.value as SalesByCountryResponse)?.data || []
            : [];

        const nextReward = totalsData?.total_amount
          ? calculateRewardProgress(
              totalsData.total_amount,
              totalsData.total_amount_formatted,
              currency,
              totalsData.conversions
            )
          : createDefaultReward();

        return {
          reports: reportsData,
          paidSales: paidSalesData,
          metrics: metricsData,
          nextReward,
          totalAmount: totalsData?.total_amount_formatted,
          retention: retentionData as RetentionResponse,
          salesByCountry: salesByCountryData,
        };
      } catch (error) {
        console.error('Error fetching metrics data:', error);

        return {
          reports: [],
          paidSales: null,
          metrics: null,
          nextReward: createDefaultReward(),
          retention: { items: [] },
          salesByCountry: [],
        };
      }
    },
    ...QUERY_CONFIG,
  });

  const handleDateRangeChange = useCallback(
    (range: DateRange | undefined) => {
      setDateRange(range);
    },
    [setDateRange]
  );

  return {
    metrics: metricsData?.metrics,
    salesByCountry: metricsData?.salesByCountry || [],
    reports: metricsData?.reports || [],
    paidSales: metricsData?.paidSales,
    retention: metricsData?.retention || [],
    handleDateRangeChange,
    dateRange,
    nextReward: metricsData?.nextReward,
    isLoading,
    error,
    currency,
    totalAmount: metricsData?.totalAmount,
  };
}
